import React, { useState, useEffect } from 'react';
import shrinkImage from './shrinkImage';

// คู่มือแก้ปัญหา (ระบบซ่อมบำรุง) -- ใช้ใน MaintenanceWork
//   GuideList   รายการ + ค้นหา (แม่บ้าน: คู่มือที่แม่บ้านเห็น · ช่าง: ทั้งหมด)
//   GuideViewer อ่านทีละขั้น + "แก้ได้แล้ว" / "ยังไม่หาย"
//   GuideEditor ช่างบันทึกเป็นคู่มือจากใบงานที่ตัวเองซ่อม (ส่งให้แอดมินตรวจก่อนเผยแพร่)
// API: /api/staff/v1/maintenance/guides* (mt = axios ที่ใส่ token แล้ว)

const MAX_VIDEO_MB = 100;
const input = { boxSizing: 'border-box', width: '100%', border: '1.5px solid #CBD4CC', borderRadius: 10, padding: 12, fontSize: 16 };

// อัปรูป (ย่อก่อน) / วิดีโอ -> { url, media_type }
export const uploadMedia = async (mt, file) => {
  const isVideo = /^video\//.test(file.type || '');
  if (isVideo && file.size > MAX_VIDEO_MB * 1024 * 1024) throw new Error(`วิดีโอใหญ่เกิน ${MAX_VIDEO_MB}MB — ถ่ายสั้นลงหน่อยนะคะ`);
  const body = isVideo ? file : await shrinkImage(file);
  const fd = new FormData();
  fd.append('file', body, isVideo ? (file.name || 'clip.mp4') : 'photo.jpg');
  const r = await mt('upload', { method: 'post', data: fd, timeout: 180000 });
  return { url: r.data.url, media_type: r.data.media_type };
};

const Media = ({ m }) => {
  if (m.media_type === 'video') return <video src={m.url} controls playsInline style={{ width: '100%', borderRadius: 12, background: '#000', marginTop: 10 }} />;
  if (m.media_type === 'link') {
    return <a href={m.url} target="_blank" rel="noreferrer" style={{ display: 'block', marginTop: 10, padding: 12, borderRadius: 12, background: '#EEF4FB', color: '#2C6DAF', fontWeight: 800, textAlign: 'center', textDecoration: 'none' }}>▶️ เปิดวิดีโอ</a>;
  }
  return <a href={m.url} target="_blank" rel="noreferrer"><img src={m.url} alt="" style={{ width: '100%', borderRadius: 12, marginTop: 10, display: 'block' }} /></a>;
};

export const GuideCard = ({ g, S, onOpen }) => (
  <div style={{ ...S.card, display: 'flex', gap: 12, alignItems: 'center', cursor: 'pointer', marginBottom: 8 }} onClick={() => onOpen(g.id)}>
    {g.cover
      ? <img src={g.cover} alt="" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 10, flexShrink: 0 }} />
      : <div style={{ width: 60, height: 60, borderRadius: 10, background: '#EEF4FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, flexShrink: 0 }}>📘</div>}
    <div style={{ minWidth: 0, flex: 1 }}>
      <div style={{ fontWeight: 800 }}>{g.title}</div>
      {g.symptom && <div style={{ fontSize: 13, color: '#586159', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.symptom}</div>}
      <div style={{ fontSize: 12, color: '#8A938B' }}>
        {g.step_count} ขั้น{g.has_video ? ' · 🎬 มีวิดีโอ' : ''}{g.for_this_room ? ' · ห้องนี้' : ''}{g.audience === 'technician' ? ' · ช่าง' : ''}
      </div>
    </div>
    <div style={{ color: '#8A938B', fontSize: 22 }}>›</div>
  </div>
);

export function GuideList({ mt, S, onOpen }) {
  const [q, setQ] = useState('');
  const [list, setList] = useState(null);
  const load = (query = '') => {
    setList(null);
    mt(`guides${query ? `?q=${encodeURIComponent(query)}` : ''}`).then((r) => setList(r.data || [])).catch(() => setList([]));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  return (
    <>
      <form style={{ display: 'flex', gap: 6, marginBottom: 12 }} onSubmit={(e) => { e.preventDefault(); load(q.trim()); }}>
        <input style={input} placeholder="ค้นหาอาการ เช่น น้ำไม่อุ่น" value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="submit" style={{ ...S.back, color: '#2C6DAF' }}>ค้นหา</button>
      </form>
      {list === null && <div style={{ textAlign: 'center', color: '#8A938B', padding: 20 }}>กำลังโหลด…</div>}
      {list && list.length === 0 && <div style={{ ...S.card, textAlign: 'center', color: '#8A938B' }}>ยังไม่มีคู่มือ{q ? 'ที่ตรงกับคำค้น' : ''}</div>}
      {(list || []).map((g) => <GuideCard key={g.id} g={g} S={S} onOpen={onOpen} />)}
    </>
  );
}

/**
 * askHelped = มาจากหน้าแจ้งปัญหา -> โชว์ปุ่ม "แก้ได้แล้ว" / "ยังไม่หาย แจ้งช่าง"
 * onFeedback(helped) เรียกหลังบันทึกผลแล้ว
 */
export function GuideViewer({ mt, S, guideId, roomId, askHelped, onBack, onFeedback }) {
  const [g, setG] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    mt(`guides/${guideId}`).then((r) => setG(r.data)).catch((e) => setErr(e?.response?.data?.message || 'เปิดคู่มือไม่ได้'));
  }, [guideId]); // eslint-disable-line

  const send = (helped) => {
    if (busy) return;
    setBusy(true);
    mt(`guides/${guideId}/feedback`, { method: 'post', data: { helped, room_id: roomId || null } })
      .catch(() => {}) // บันทึกสถิติไม่ได้ ไม่ต้องขวางการใช้งาน
      .finally(() => { setBusy(false); onFeedback(helped); });
  };

  return (
    <div style={S.page}>
      <div style={S.bar}>
        <button style={S.back} onClick={onBack}>‹ กลับ</button>
        <div style={{ minWidth: 0 }}><h1 style={{ ...S.h, fontSize: 17 }}>📘 {g?.title || 'คู่มือ'}</h1>
          {g?.category && <div style={S.sub}>{g.category.icon} {g.category.name}</div>}</div>
      </div>
      {err && <div style={{ ...S.card, color: '#CD3E33' }}>{err}</div>}
      {!g && !err && <div style={{ textAlign: 'center', color: '#8A938B', padding: 20 }}>กำลังโหลด…</div>}
      {g && (
        <>
          {g.symptom && <div style={{ ...S.card, background: '#F7F9F8' }}><b>อาการ:</b> {g.symptom}</div>}
          {g.safety_warning && (
            <div style={{ ...S.card, background: '#FFF3F2', borderColor: '#F3C6C1', color: '#A3271C', fontWeight: 700 }}>⚠️ {g.safety_warning}</div>
          )}
          {(g.steps || []).map((s) => (
            <div key={s.id} style={S.card}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ width: 32, height: 32, borderRadius: 99, background: '#0F8A56', color: '#fff', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{s.step_no}</div>
                <div style={{ flex: 1, minWidth: 0, fontSize: 17, whiteSpace: 'pre-wrap', paddingTop: 4 }}>{s.text}</div>
              </div>
              {(s.media || []).map((m, i) => <Media key={`${m.url}-${i}`} m={m} />)}
            </div>
          ))}
          {askHelped && (
            <>
              <button disabled={busy} style={S.bigBtn('#0F8A56')} onClick={() => send(true)}>✅ แก้ได้แล้ว ไม่ต้องเรียกช่าง</button>
              <button disabled={busy} style={S.bigBtn('#CD3E33')} onClick={() => send(false)}>❌ ยังไม่หาย แจ้งช่าง</button>
            </>
          )}
        </>
      )}
    </div>
  );
}

// ช่างบันทึกเป็นคู่มือ -- เติมขั้นตอนจากใบงาน (สิ่งที่ทำ + รูปหลังซ่อม) ให้ก่อน แก้ได้
export function GuideEditor({ mt, S, ticket, onCancel, onDone }) {
  const after = (ticket.media || []).filter((m) => m.kind === 'after').map((m) => ({ url: m.url, media_type: m.media_type }));
  const [title, setTitle] = useState(ticket.title || '');
  const [symptom, setSymptom] = useState(ticket.description || '');
  const [warning, setWarning] = useState('');
  const [thisRoomOnly, setThisRoomOnly] = useState(!!ticket.room_id);
  const [steps, setSteps] = useState([{ text: ticket.resolution_note || '', media: after }]);
  const [uploading, setUploading] = useState(0);
  const [busy, setBusy] = useState(false);

  const setStep = (i, patch) => setSteps((x) => x.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const pick = async (i, fileList) => {
    for (const file of [...(fileList || [])]) { // eslint-disable-line no-restricted-syntax
      setUploading((n) => n + 1);
      try {
        const m = await uploadMedia(mt, file); // eslint-disable-line no-await-in-loop
        setSteps((x) => x.map((s, j) => (j === i ? { ...s, media: [...s.media, m] } : s)));
      } catch (e) { alert(e.message && e.message.includes('MB') ? e.message : 'ส่งไฟล์ไม่สำเร็จ — ลองใหม่อีกครั้ง'); }
      setUploading((n) => n - 1);
    }
  };
  const submit = () => {
    if (busy) return;
    if (uploading) { alert('รอรูปส่งเสร็จก่อนนะคะ'); return; }
    if (!title.trim()) { alert('ใส่ชื่อคู่มือ'); return; }
    if (!steps.some((s) => s.text.trim() || s.media.length)) { alert('ใส่ขั้นตอนอย่างน้อย 1 ขั้น'); return; }
    setBusy(true);
    mt(`tickets/${ticket.id}/guide`, { method: 'post', data: { title, symptom, safety_warning: warning, this_room_only: thisRoomOnly, steps } })
      .then(() => { setBusy(false); onDone(); })
      .catch((e) => { setBusy(false); alert(e?.response?.data?.message || 'ส่งไม่สำเร็จ — ลองอีกครั้ง'); });
  };

  return (
    <div style={S.page}>
      <div style={S.bar}>
        <button style={S.back} onClick={onCancel}>‹ ยกเลิก</button>
        <div><h1 style={{ ...S.h, fontSize: 17 }}>📘 บันทึกเป็นคู่มือ</h1><div style={S.sub}>สอนแม่บ้านแก้เองครั้งหน้า · แอดมินตรวจก่อนเผยแพร่</div></div>
      </div>
      <div style={S.card}>
        <div style={{ fontWeight: 800, marginBottom: 6 }}>ชื่อคู่มือ</div>
        <input style={input} placeholder="เช่น น้ำไม่อุ่น — รีเซ็ตเบรกเกอร์" value={title} onChange={(e) => setTitle(e.target.value)} />
        <div style={{ fontWeight: 800, margin: '12px 0 6px' }}>อาการที่เห็น</div>
        <input style={input} placeholder="เช่น ไฟเครื่องทำน้ำอุ่นไม่ติด" value={symptom} onChange={(e) => setSymptom(e.target.value)} />
        <div style={{ fontWeight: 800, margin: '12px 0 6px' }}>⚠️ ข้อห้าม (ถ้ามี)</div>
        <input style={input} placeholder="เช่น ห้ามเปิดฝาเครื่อง" value={warning} onChange={(e) => setWarning(e.target.value)} />
        {ticket.room_id && (
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, fontSize: 15 }}>
            <input type="checkbox" checked={thisRoomOnly} onChange={(e) => setThisRoomOnly(e.target.checked)} style={{ width: 20, height: 20 }} />
            ใช้เฉพาะห้องนี้ ({ticket.room?.name_th}) — ไม่ติ๊ก = ทุกห้อง
          </label>
        )}
      </div>
      {steps.map((s, i) => (
        <div key={i} style={S.card}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
            <b style={{ flex: 1 }}>ขั้นที่ {i + 1}</b>
            {steps.length > 1 && <button style={{ ...S.back, color: '#CD3E33', padding: '4px 10px' }} onClick={() => setSteps(steps.filter((_, j) => j !== i))}>ลบ</button>}
          </div>
          <textarea rows={3} style={input} placeholder="ทำอะไร เช่น เปิดตู้ไฟหน้าห้อง ดันเบรกเกอร์ตัวที่เขียนว่า 'น้ำอุ่น' ขึ้น" value={s.text} onChange={(e) => setStep(i, { text: e.target.value })} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
            {s.media.map((m, k) => (
              <div key={`${m.url}-${k}`} style={{ position: 'relative' }}>
                {m.media_type === 'video'
                  ? <video src={m.url} style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 10, background: '#000' }} />
                  : <img src={m.url} alt="" style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 10 }} />}
                <button onClick={() => setStep(i, { media: s.media.filter((_, j) => j !== k) })} style={{ position: 'absolute', top: -6, right: -6, border: 'none', borderRadius: 99, width: 24, height: 24, background: '#CD3E33', color: '#fff', fontWeight: 800 }}>×</button>
              </div>
            ))}
          </div>
          <label style={{ ...S.back, display: 'block', textAlign: 'center', marginTop: 8, padding: 10, color: '#2C6DAF' }}>
            📷 เพิ่มรูป / วิดีโอ
            <input type="file" accept="image/*,video/*" multiple style={{ display: 'none' }} onChange={(e) => { pick(i, e.target.files); e.target.value = ''; }} />
          </label>
        </div>
      ))}
      <button style={{ ...S.back, width: '100%', padding: 12, color: '#0F8A56' }} onClick={() => setSteps([...steps, { text: '', media: [] }])}>+ เพิ่มขั้นตอน</button>
      {uploading > 0 && <div style={{ textAlign: 'center', color: '#586159', marginTop: 8 }}>กำลังส่งไฟล์ {uploading}…</div>}
      <button disabled={busy || uploading > 0} style={S.bigBtn('#2C6DAF')} onClick={submit}>{busy ? 'กำลังส่ง…' : 'ส่งให้แอดมินตรวจ'}</button>
    </div>
  );
}
